/**
 * planit.family Design System Theme
 *
 * Core design tokens shared between web (Next.js) and mobile (React Native).
 */

export const theme = {
  colors: {
    // Brand Colors
    primary: {
      gold: '#F5A623',
      navy: '#2C4A6B',
      terracotta: '#C86B3E',
    },
    accent: {
      teal: '#6ABFAB',
      lightBlue: '#8ECAE6',
      rose: '#C97B7B',
    },
    // Semantic Colors - aligned with Radix color scale step 9
    semantic: {
      success: '#12a594', // teal-9
      warning: '#ffc53d', // amber-9
      error: '#e5484d',   // red-9
      info: '#7ce2fe',    // sky-9
    },
    // Neutral Palette - aligned with Radix slate scale
    neutral: {
      50: '#f9f9fb',   // slate-2  app backgrounds
      100: '#f0f0f3',  // slate-3  subtle backgrounds
      200: '#e0e1e6',  // slate-5  UI element backgrounds
      300: '#d9d9e0',  // slate-6  borders / separators
      400: '#cdced6',  // slate-7  hover borders / strong separators
      500: '#8b8d98',  // slate-9  secondary text / icons
      600: '#80838d',  // slate-10 medium text
      700: '#60646c',  // slate-11 low-contrast text
      800: '#1c2024',  // slate-12 high-contrast text
      900: '#1c2024',  // slate-12 (slate has no step beyond 12)
    },
    white: '#FFFFFF',
    black: '#000000',
    // Text Colors (shortcuts)
    text: {
      primary: '#1c2024',  // slate-12 high-contrast text
      secondary: '#60646c', // slate-11 low-contrast text
      disabled: '#8b8d98',  // slate-9  placeholder / disabled
      inverse: '#FFFFFF',
    },
    // Background Colors (shortcuts)
    background: {
      primary: '#FFFFFF',
      secondary: '#f9f9fb', // slate-2
      tertiary: '#f0f0f3',  // slate-3
    },
    // Sidebar Colors (used by collapsible nav)
    sidebar: {
      bg: '#2C4A6B',
      text: '#FFFFFF',
      textMuted: 'rgba(255,255,255,0.65)',
      active: '#F5A623',
      hover: 'rgba(255,255,255,0.08)',
    },
  },

  // Spacing Scale (4px base unit)
  space: {
    1: '0.25rem', // 4px
    2: '0.5rem', // 8px
    3: '0.75rem', // 12px
    4: '1rem', // 16px
    6: '1.5rem', // 24px
    8: '2rem', // 32px
    12: '3rem', // 48px
    16: '4rem', // 64px
    24: '6rem', // 96px
    32: '8rem', // 128px
  },

  // Border Radius (toned-down scale)
  radius: {
    sm: '0.25rem', // 4px
    md: '0.375rem', // 6px
    lg: '0.5rem',   // 8px
    xl: '0.75rem',  // 12px
    '2xl': '1rem',  // 16px
    full: '9999px', // Circular
  },

  // Shadows
  shadow: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },

  // Focus Rings
  focus: {
    ring: '0 0 0 3px rgba(238, 173, 43, 0.4)',
    offset: '2px',
  },

  // Transitions
  transition: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '250ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '400ms cubic-bezier(0.4, 0, 0.2, 1)',
  },

  // Easing
  easing: {
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },

  // Typography
  font: {
    family: {
      base: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
      heading: "'Noto Serif', Georgia, 'Times New Roman', serif",
      mono: "'SF Mono', ui-monospace, 'Cascadia Code', 'Source Code Pro', Menlo, Consolas, 'Courier New', monospace",
    },
    size: {
      display: '3.75rem', // 60px
      h1: '2.25rem', // 36px
      h2: '1.875rem', // 30px
      h3: '1.5rem', // 24px
      h4: '1.25rem', // 20px
      h5: '1.125rem', // 18px
      h6: '1rem', // 16px
      bodyLg: '1.125rem', // 18px
      body: '1rem', // 16px
      bodySm: '0.875rem', // 14px
      caption: '0.75rem', // 12px
      button: '1rem', // 16px
    },
    weight: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.1,
      snug: 1.2,
      normal: 1.5,
      relaxed: 1.6,
    },
    letterSpacing: {
      tight: '-0.02em',
      normal: '0',
      wide: '0.01em',
    },
  },

  // Breakpoints (for use with min-width media queries on web)
  breakpoint: {
    xs: '0px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },

  // Z-Index Scale
  zIndex: {
    base: 0,
    dropdown: 100,
    sticky: 200,
    fixed: 300,
    modalBackdrop: 400,
    modal: 500,
    popover: 600,
    tooltip: 700,
  },
} as const;

export type Theme = typeof theme;

// ─── Warm & Cozy Theme Palettes ────────────────────────────────────────────
// Light and dark color overrides for the "Warm & Cozy" design theme.

export const warmCozyLight = {
  ...theme,
  colors: {
    ...theme.colors,
    primary: {
      ...theme.colors.primary,
      gold: '#EEAD2B',
      navy: '#0F49BD',
    },
    background: {
      primary: '#F8F9FA',
      secondary: '#F0F2F5',
      tertiary: '#E8EBF0',
    },
    text: {
      primary: '#1A2340',
      secondary: '#4A5568',
      disabled: '#9AA3B0',
      inverse: '#FFFFFF',
    },
    sidebar: {
      bg: '#0F49BD',
      text: '#FFFFFF',
      textMuted: 'rgba(255,255,255,0.65)',
      active: '#EEAD2B',
      hover: 'rgba(255,255,255,0.1)',
    },
  },
};

export const warmCozyDark = {
  ...theme,
  colors: {
    ...theme.colors,
    primary: {
      ...theme.colors.primary,
      gold: '#F4C025',
      navy: '#5B9CF6',
    },
    background: {
      primary: '#0D1B2E',
      secondary: '#132238',
      tertiary: '#1A2E48',
    },
    neutral: {
      ...theme.colors.neutral,
      50: '#1A2E48',
      100: '#1F3550',
      200: '#284460',
      300: '#375572',
      400: '#4A6A88',
      500: '#5E819E',
      600: '#7A9AB8',
      700: '#9DB8D9',
      800: '#C5D8EE',
      900: '#E8F0FE',
    },
    text: {
      primary: '#E8F0FE',
      secondary: '#9DB8D9',
      disabled: '#5A7A9A',
      inverse: '#0D1B2E',
    },
    sidebar: {
      bg: '#0A1628',
      text: '#E8F0FE',
      textMuted: 'rgba(232,240,254,0.55)',
      active: '#F4C025',
      hover: 'rgba(255,255,255,0.08)',
    },
  },
  shadow: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
  },
};

export type WarmCozyTheme = typeof warmCozyLight;

// Web-only helpers (safe to import in web, unused in native)
export const media = {
  sm: `@media (min-width: ${theme.breakpoint.sm})`,
  md: `@media (min-width: ${theme.breakpoint.md})`,
  lg: `@media (min-width: ${theme.breakpoint.lg})`,
  xl: `@media (min-width: ${theme.breakpoint.xl})`,
  '2xl': `@media (min-width: ${theme.breakpoint['2xl']})`,
} as const;

export const utils = {
  focusRing: (t: Theme) => `
    outline: none;
    box-shadow: ${t.focus.ring};
    outline-offset: ${t.focus.offset};
  `,
  reducedMotion: (styles: string) => `
    @media (prefers-reduced-motion: reduce) {
      ${styles}
    }
  `,
};
